<template>
    <div class="step3-registration">
        <!-- Registration open date -->
        <div class="field">
            <label class="label">{{ translate("wizard.registration.opens_label") }}</label>
            <div class="buttons has-addons mb-0">
                <button
                    class="button is-small"
                    :class="{ 'is-primary': !customOpenDate && openDateOffset === null }"
                    @click="setOpenDatePreset(null)"
                >
                    {{ translate("wizard.registration.opens_today") }}
                </button>
                <button
                    class="button is-small"
                    :class="{ 'is-primary': !customOpenDate && openDateOffset === 7 }"
                    @click="setOpenDatePreset(7)"
                >
                    {{ translate("wizard.registration.opens_in_1_week") }}
                </button>
                <button
                    class="button is-small"
                    :class="{ 'is-primary': !customOpenDate && openDateOffset === 14 }"
                    @click="setOpenDatePreset(14)"
                >
                    {{ translate("wizard.registration.opens_in_2_weeks") }}
                </button>
                <button class="button is-small" :class="{ 'is-primary': customOpenDate }" @click="enableCustomOpenDate">
                    {{ translate("wizard.registration.custom") }}
                </button>
            </div>
            <div v-if="customOpenDate" class="control mt-2" style="max-width: 200px">
                <DateTimePicker
                    class="input is-small"
                    :class="{ 'is-danger': !registrationOpenDateIsValid }"
                    v-model="customOpenDateValue"
                    :min-date="todayIso"
                    :max-date="openDatePickerMaxDate ?? undefined"
                />
            </div>
        </div>

        <!-- Close-gap presets -->
        <div class="field mt-4">
            <label class="label">{{ translate("wizard.registration.close_gap_label") }}</label>
            <div class="buttons has-addons mb-0">
                <button
                    v-for="preset in CLOSE_GAP_PRESETS"
                    :key="preset"
                    class="button is-small"
                    :class="{ 'is-primary': closeGapDays === preset && !customGap }"
                    @click="setCloseGap(preset)"
                >
                    {{
                        preset === 1
                            ? translate("wizard.registration.day_before")
                            : translate("wizard.registration.days_before", false, [preset])
                    }}
                </button>
                <button class="button is-small" :class="{ 'is-primary': customGap }" @click="enableCustomGap">
                    {{ translate("wizard.registration.custom") }}
                </button>
            </div>
            <div v-if="customGap" class="control mt-2" style="max-width: 160px">
                <input
                    v-model.number="closeGapDays"
                    class="input is-small"
                    :class="{ 'is-danger': !closeGapIsValid }"
                    type="number"
                    min="0"
                    step="1"
                    @change="onCloseGapChanged"
                />
            </div>
        </div>

        <!-- Stage count -->
        <div class="field mt-4">
            <label class="label">{{ translate("wizard.registration.how_many_stages") }}</label>
            <p class="help mb-3">{{ translate("wizard.registration.stages_helper") }}</p>
            <div class="buttons has-addons mb-0">
                <button
                    v-for="n in [1, 2, 3]"
                    :key="n"
                    class="button is-small"
                    :class="{ 'is-primary': stageCount === n }"
                    @click="setStageCount(n)"
                >
                    {{ n }}
                </button>
            </div>
        </div>

        <!-- Per-stage rows -->
        <div class="mt-4">
            <div v-for="(_, i) in stageDurations" :key="i" class="field">
                <label class="label is-small mb-1">{{ stageLabel(i) }}</label>
                <div class="is-flex is-align-items-center" style="gap: 0.75rem">
                    <div class="control" style="max-width: 100px">
                        <input
                            v-model.number="stageDurations[i]"
                            class="input is-small"
                            :class="{ 'is-danger': !isIntegerGreaterThan0(stageDurations[i]) }"
                            type="number"
                            min="1"
                            step="1"
                        />
                    </div>
                    <span class="has-text-grey-light is-size-7">{{ translate("wizard.registration.days") }}</span>
                    <span v-if="startDate && stageDateHint(i)" class="has-text-grey-light is-size-7"> &mdash; {{ stageDateHint(i) }} </span>
                </div>
            </div>
        </div>

        <!-- No start date notice -->
        <p v-if="!startDate" class="help has-text-grey mt-3">
            {{ translate("wizard.registration.no_event_date") }}
        </p>

        <!-- Footer gap indicator -->
        <p v-else-if="numericValuesAreValid && isValid" class="help has-text-success mt-3">
            <i class="fas fa-check mr-1"></i>
            {{
                actualCloseGapDays === 1
                    ? translate("wizard.registration.closes_1_day_before")
                    : translate("wizard.registration.closes_n_days_before", false, [actualCloseGapDays])
            }}
        </p>
        <p v-else-if="numericValuesAreValid" class="help has-text-danger mt-3">
            <i class="fas fa-times mr-1"></i>
            {{ translate("wizard.registration.overlap_error", false, [overlapDays]) }}
        </p>

        <!-- Navigation -->
        <div class="field mt-5">
            <div class="buttons">
                <button class="button" @click="emit('back')"><i class="fas fa-arrow-left mr-2"></i>{{ translate("common.back") }}</button>
                <button class="button is-primary is-flex-grow-1" data-testid="wizard-continue" :disabled="!isValid" @click="onSubmit">
                    {{ translate("common.continue") }}<i class="fas fa-arrow-right ml-2"></i>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { DateTime } from "luxon";
    import { computed, onMounted, ref, watch } from "vue";
    import { translate } from "../../../Common/translations";
    import { useWizardStore } from "../wizardStore";
    import DateTimePicker from "../../../Common/DateTimePicker.vue";
    import { getCurrentDateTimeInTimezone, parseDateTimeInTimezone, parseUtcDateTimeInTimezone } from "../../../Common/dateTimeUtils";
    import { isIntegerGreaterThan0, isIntegerGreaterThanOrEqualTo0 } from "../../../Common/numbers";

    const emit = defineEmits<{
        submit: [];
        back: [];
    }>();

    const wizard = useWizardStore();

    const CLOSE_GAP_PRESETS = [1, 3, 5] as const;
    const STAGE_LABELS = [
        "wizard.registration.stage_early_bird",
        "wizard.registration.stage_normal",
        "wizard.registration.stage_late",
    ] as const;

    const closeGapDays = ref(5);
    const customGap = ref(false);
    const stageCount = ref<1 | 2 | 3>(2);
    const stageDurations = ref<number[]>([]);

    const openDateOffset = ref<number | null>(null);
    const customOpenDate = ref(false);
    const customOpenDateValue = ref<string>("");
    let initialized = false;

    const startDate = computed(() => wizard.startDate || null);
    const today = computed(() => getCurrentDateTimeInTimezone(wizard.timezone).startOf("day"));
    const eventDay = computed(() => {
        if (!startDate.value) {
            return null;
        }
        return parseUtcDateTimeInTimezone(startDate.value, wizard.timezone).startOf("day");
    });

    const daysUntilEvent = computed(() => {
        if (!eventDay.value) {
            return null;
        }
        return eventDay.value.diff(today.value, "days").days;
    });

    const registrationOpenDateBase = computed((): DateTime => {
        if (customOpenDate.value && customOpenDateValue.value) {
            return parseDateTimeInTimezone(customOpenDateValue.value, "yyyy-MM-dd", wizard.timezone).startOf("day");
        }
        if (openDateOffset.value !== null) {
            return today.value.plus({ days: openDateOffset.value });
        }
        return today.value;
    });

    const openDateOffsetDays = computed((): number => {
        return registrationOpenDateBase.value.diff(today.value, "days").days;
    });

    const registrationOpenDateString = computed((): string | null => {
        if (openDateOffsetDays.value === 0) {
            return null;
        }
        return registrationOpenDateBase.value.toFormat("yyyy-MM-dd");
    });

    const availableWindow = computed(() => {
        if (daysUntilEvent.value === null || !closeGapIsValid.value) {
            return null;
        }
        return Math.max(0, daysUntilEvent.value - closeGapDays.value - openDateOffsetDays.value);
    });

    const todayIso = computed(() => today.value.toFormat("yyyy-MM-dd"));

    const openDatePickerMaxDate = computed((): string | null => {
        if (!eventDay.value || !closeGapIsValid.value) {
            return null;
        }
        // Bound by stageCount (min 1 day/stage), not totalDuration: totalDuration reflects
        // durations sized for the *current* open date, and applySmartDefaults() will resize
        // them once a later open date is picked. Using totalDuration here would block dates
        // that become valid again after that resize.
        return eventDay.value.minus({ days: closeGapDays.value + stageCount.value }).toFormat("yyyy-MM-dd");
    });

    const closeGapIsValid = computed(() => isIntegerGreaterThanOrEqualTo0(closeGapDays.value));
    const registrationOpenDateIsValid = computed(() => {
        if (!customOpenDate.value) {
            return true;
        }
        return customOpenDateValue.value !== "" && registrationOpenDateBase.value.isValid && openDateOffsetDays.value >= 0;
    });
    const stageDurationsAreValid = computed(
        () => stageDurations.value.length >= 1 && stageDurations.value.length <= 3 && stageDurations.value.every(isIntegerGreaterThan0),
    );
    const numericValuesAreValid = computed(() => closeGapIsValid.value && stageDurationsAreValid.value);

    function suggestStageCount(window: number): 1 | 2 | 3 {
        if (window >= 45) {
            return 3;
        }
        if (window >= 21) {
            return 2;
        }
        return 1;
    }

    function computeDefaults(count: 1 | 2 | 3, window: number): number[] {
        if (count === 1) {
            return [Math.max(1, window)];
        }
        if (count === 2) {
            // Normal gets ~75%, Late gets ~25% — normal should be the majority
            const late = Math.max(1, Math.round(window * 0.25));
            const normal = Math.max(1, window - late);
            return [normal, late];
        }
        // 3 stages: Early=2/8, Normal=5/8, Late=1/8 (ratios 2:5:1)
        const late = Math.max(1, Math.round(window / 8));
        const early = Math.max(1, Math.round((window * 2) / 8));
        const normal = Math.max(1, window - early - late);
        return [early, normal, late];
    }

    function applySmartDefaults() {
        const window = availableWindow.value ?? 30;
        stageDurations.value = computeDefaults(stageCount.value, window);
    }

    function setCloseGap(days: number) {
        closeGapDays.value = days;
        customGap.value = false;
        onCloseGapChanged();
    }

    function enableCustomGap() {
        customGap.value = true;
    }

    function onCloseGapChanged() {
        if (!closeGapIsValid.value) {
            return;
        }
        applySmartDefaults();
    }

    watch(
        customOpenDateValue,
        value => {
            if (initialized && customOpenDate.value && value) {
                onCloseGapChanged();
            }
        },
        { flush: "sync" },
    );

    watch(
        [stageDurations, customOpenDateValue, openDateOffset, customOpenDate],
        () => {
            if (!initialized) {
                return;
            }
            syncCloseGapFromSchedule();
            syncRegistrationState();
        },
        { deep: true },
    );

    function setOpenDatePreset(preset: number | null) {
        customOpenDate.value = false;
        openDateOffset.value = preset;
        onCloseGapChanged();
    }

    function enableCustomOpenDate() {
        customOpenDate.value = true;
        openDateOffset.value = null;
        onCloseGapChanged();
    }

    function setStageCount(n: number) {
        stageCount.value = n as 1 | 2 | 3;
        applySmartDefaults();
    }

    function stageLabel(index: number): string {
        // Labels depend on stage count:
        //   1 stage  → [Normal]              (offset 1)
        //   2 stages → [Normal, Late]        (offset 1)
        //   3 stages → [Early bird, Normal, Late]  (offset 0)
        const offset = stageCount.value === 3 ? 0 : 1;
        return translate(STAGE_LABELS[index + offset] ?? STAGE_LABELS[STAGE_LABELS.length - 1]) ?? "";
    }

    function stageStartDay(index: number): number {
        return stageDurations.value.slice(0, index).reduce((acc, d) => acc + d, 0);
    }

    function stageDateHint(index: number): string | null {
        if (!startDate.value) {
            return null;
        }
        const base = registrationOpenDateBase.value;
        const openOffset = stageStartDay(index);
        const closeOffset = openOffset + (stageDurations.value[index] ?? 0);
        const locale = (window as any).panelLocale ?? "en";
        const fmt = new Intl.DateTimeFormat(locale, { month: "long", day: "numeric", timeZone: wizard.timezone });
        const openDate = fmt.format(base.plus({ days: openOffset }).toJSDate());
        const closeDate = fmt.format(base.plus({ days: closeOffset - 1 }).toJSDate());
        return `${openDate} → ${closeDate}`;
    }

    const totalDuration = computed(() => stageDurations.value.reduce((a, b) => a + b, 0));

    const actualCloseGapDays = computed(() => {
        if (daysUntilEvent.value === null) {
            return closeGapDays.value;
        }
        return daysUntilEvent.value - openDateOffsetDays.value - totalDuration.value;
    });

    const overlapDays = computed(() => {
        if (daysUntilEvent.value === null) {
            return 0;
        }
        return Math.max(0, -actualCloseGapDays.value);
    });

    const isValid = computed(() => {
        if (!numericValuesAreValid.value || !registrationOpenDateIsValid.value) {
            return false;
        }
        if (daysUntilEvent.value === null) {
            return true;
        }
        return actualCloseGapDays.value >= 0;
    });

    function onSubmit() {
        syncRegistrationState();
        emit("submit");
    }

    function syncCloseGapFromSchedule() {
        if (daysUntilEvent.value === null || !stageDurationsAreValid.value) {
            return;
        }
        const resultingGap = actualCloseGapDays.value;
        if (!isIntegerGreaterThanOrEqualTo0(resultingGap)) {
            return;
        }
        closeGapDays.value = resultingGap;
        customGap.value = !CLOSE_GAP_PRESETS.some(preset => preset === resultingGap);
    }

    function syncRegistrationState() {
        wizard.registrationStages = stageDurations.value.map(durationDays => ({ durationDays }));
        wizard.registrationOpenDate = registrationOpenDateString.value;
        wizard.registrationConfigured = true;
    }

    function restoreRegistrationState() {
        const savedDurations = wizard.registrationStages.map(stage => stage.durationDays);
        if (savedDurations.length < 1 || savedDurations.length > 3) {
            return false;
        }

        stageCount.value = savedDurations.length as 1 | 2 | 3;
        stageDurations.value = savedDurations;

        if (wizard.registrationOpenDate === null) {
            customOpenDate.value = false;
            openDateOffset.value = null;
        } else {
            const savedOpenDate = parseDateTimeInTimezone(wizard.registrationOpenDate, "yyyy-MM-dd", wizard.timezone).startOf("day");
            const savedOffset = savedOpenDate.diff(today.value, "days").days;
            const matchingPreset = savedOffset === 7 || savedOffset === 14 ? savedOffset : null;

            customOpenDate.value = matchingPreset === null;
            openDateOffset.value = matchingPreset;
            customOpenDateValue.value = matchingPreset === null ? wizard.registrationOpenDate : "";
        }

        syncCloseGapFromSchedule();
        return true;
    }

    onMounted(() => {
        const restored = wizard.registrationConfigured && restoreRegistrationState();
        if (!restored) {
            if (availableWindow.value !== null) {
                stageCount.value = suggestStageCount(availableWindow.value);
            }
            applySmartDefaults();
        }
        initialized = true;
        syncRegistrationState();
    });
</script>
